import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeftIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
  ArrowTopRightOnSquareIcon,
  XMarkIcon,
  UserIcon,
  ShieldCheckIcon,
  ClockIcon,
  UserGroupIcon,
  MapPinIcon,
  InboxIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ClipboardIcon,
  ArrowPathIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  PencilSquareIcon,
  PaperAirplaneIcon,
  RocketLaunchIcon
} from "@heroicons/react/24/outline";
import axios from "axios";
import dayjs from "dayjs";
import clsx from "clsx";
import { BASE_URL } from "../../config";
import PlanTypeInfoTooltip_v2 from "../PlanTypeInfoTooltip_v2";
import Swal from "sweetalert2";

const isValidDateValue = (value) => {
  if (!value) return false;
  if (typeof value === "string" && value.startsWith("0000-00-00")) {
    return false;
  }
  const m = dayjs(value);
  return m.isValid();
};

const formatDate = (d, withTime = false, emptyValue = "-") => {
  if (!isValidDateValue(d)) return emptyValue;
  const m = dayjs(d);
  return withTime ? m.format("MMM D, YYYY h:mm A") : m.format("MMM D, YYYY");
};

const formatCurrency = (v) => {
  if (formatDetailText(v) === "N/A") return "N/A";
  const n = Number(v);
  if (Number.isNaN(n)) return v;
  return new Intl.NumberFormat("en-PH", {
    style: "currency",
    currency: "PHP",
    maximumFractionDigits: 2,
  }).format(n);
};

const Badge = ({ children, color = "gray" }) => {
  const colors = {
    gray: "bg-gray-100 text-gray-700 ring-gray-200",
    blue: "bg-blue-100 text-blue-700 ring-blue-200",
    green: "bg-emerald-100 text-emerald-700 ring-emerald-200",
    red: "bg-rose-100 text-rose-700 ring-rose-200",
    amber: "bg-amber-100 text-amber-700 ring-amber-200",
    violet: "bg-violet-100 text-violet-700 ring-violet-200",
  };
  return (
    <span
      className={clsx(
        "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ring-1",
        colors[color]
      )}
    >
      {children}
    </span>
  );
};

const InfoRow = ({ label, value, copyable = false }) => {
  const [copied, setCopied] = useState(false);
  const canCopy = copyable && value;
  const onCopy = async () => {
    if (!canCopy) return;
    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      toast.info("Copy failed");
    }
  };
  return (
    <div className="flex items-start justify-between gap-4 py-2">
      <div className="w-1/3 min-w-[160px] text-sm font-medium text-gray-600">
        {label}
      </div>
      <div className="flex-1 text-sm text-gray-900 break-words">
        {value ?? "—"}
        {canCopy && (
          <button
            type="button"
            onClick={onCopy}
            className="ml-2 inline-flex items-center gap-1 rounded-md border border-gray-200 px-1.5 py-0.5 text-xs text-gray-600 hover:bg-gray-50"
            title="Copy to clipboard"
          >
            {copied ? (
              <CheckCircleIcon className="h-5 w-5 text-emerald-500" />
            ) : (
              <ClipboardIcon className="h-5 w-5" />
            )}
            {copied ? "Copied" : "Copy"}
          </button>
        )}
      </div>
    </div>
  );
};

const TabButton = ({ active, onClick, label, Icon }) => (
  <button
    onClick={onClick}
    className={clsx(
      "relative inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors",
      active ? "text-blue-700" : "text-gray-600 hover:text-gray-800"
    )}
  >
    {Icon ? <Icon className="h-5 w-5" /> : null}
    <span>{label}</span>
    {active && (
      <span className="absolute inset-x-2 -bottom-1 h-0.5 rounded-full bg-blue-600" />
    )}
  </button>
);

const SkeletonBlock = () => (
  <div className="animate-pulse">
    <div className="mb-4 h-6 w-1/3 rounded bg-gray-200" />
    <div className="space-y-2">
      {[...Array(6)].map((_, i) => (
        <div key={i} className="h-4 w-full rounded bg-gray-100" />
      ))}
    </div>
  </div>
);

const EmptyState = ({
  title = "No data",
  subtitle = "Nothing to show here yet.",
}) => (
  <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50 p-10 text-center">
    <InboxIcon className="mb-2 h-8 w-8 text-gray-400" />
    <div className="text-sm font-medium text-gray-900">{title}</div>
    <div className="text-xs text-gray-500">{subtitle}</div>
  </div>
);

const TRANSACTION_EXPORT_COLUMNS = [
  { key: "trans_date", label: "TRN Date" },
  { key: "trans_no", label: "TRN No." },
  { key: "loa_no", label: "LOA No." },
  { key: "member_name", label: "Member" },
  { key: "provider_name", label: "Provider" },
  { key: "coverage_code", label: "Coverage" },
  { key: "trans_flag_desc", label: "TRN Status" },
  { key: "loa_amount", label: "LOA Amount" },
  { key: "claim_amount", label: "Claims Amount" },
  { key: "claim_status", label: "Claims Status" },
  { key: "chief_complaint", label: "Chief Complaint" },
  { key: "icd_10_col", label: "ICD-10" },
  { key: "diagnosis_col", label: "Diagnosis" },
  { key: "categ_code_col", label: "ICD Category Code" },
  { key: "categ_desc_col", label: "ICD Category" },
];

const FAMILY_MEMBER_EXPORT_COLUMNS = [
  { key: "member_id", label: "Member ID" },
  { key: "last_name", label: "Last Name" },
  { key: "first_name", label: "First Name" },
  { key: "middle_name", label: "Middle Name" },
  { key: "suffix", label: "Suffix" },
  { key: "plan_code", label: "Plan Code" },
  { key: "plan_name", label: "Plan Name" },
  { key: "date_of_birth", label: "Date of Birth" },
  { key: "member_type", label: "Member Type" },
  { key: "status", label: "Status" },
];

const STATUS_FLAG_META = {
  "0": { label: "Inactive", color: "red" },
  "1": { label: "Active/Reactivated", color: "green" },
  "2": { label: "Terminated/Cancelled", color: "gray" },
  "3": { label: "Suspended", color: "amber" },
  "4": { label: "Expired", color: "violet" },
  "5": { label: "Extended", color: "blue" },
};

const getStatusFlagMeta = (statusFlag, providedFlagDesc = "") => {
  const normalizedFlag = String(statusFlag ?? "").trim();
  const mappedMeta = STATUS_FLAG_META[normalizedFlag];
  const normalizedDesc = String(providedFlagDesc || "").trim();

  return {
    label:
      normalizedDesc ||
      mappedMeta?.label ||
      (normalizedFlag ? `Unknown (${normalizedFlag})` : "Unknown"),
    color: mappedMeta?.color || "gray",
  };
};

const renderStatusFlagBadge = (statusFlag, providedFlagDesc = "") => {
  const { label, color } = getStatusFlagMeta(statusFlag, providedFlagDesc);
  return <Badge color={color}>{label}</Badge>;
};

const formatDetailText = (value) => {
  const text = String(value ?? "").trim();
  return !text || text === "\u00e2\u20ac\u201d" ? "N/A" : text;
};

const getDisplayGender = (gender) => {
  const code = String(gender ?? "").trim().toLowerCase();
  if (code === "m") return "Male";
  if (code === "f") return "Female";
  return "-";
};

const CIVIL_STATUS_LABELS = {
  m: "Married",
  s: "Single",
  p: "Separated",
  w: "Widowed",
  d: "Divorced",
};

const getDisplayCivilStatus = (civilStatus) => {
  const code = String(civilStatus ?? "").trim().toLowerCase();
  return CIVIL_STATUS_LABELS[code] || "-";
};

const normalizeCoverageCode = (value) =>
  String(value ?? "")
    .trim()
    .toUpperCase();

const PLAN_COVERAGE_DISPLAY_CONFIG = [
  {
    title: "INPATIENT",
    items: [
      {
        key: "inpatient",
        label: "INPATIENT",
        codes: ["INPATIENT-ADMISSION", "INPATIENT-DISCHARGE"],
      },
    ],
  },
  {
    title: "OUTPATIENT",
    items: [
      {
        key: "outpatient_basic_consultation",
        label: "Basic Consultation",
        codes: ["OUTPATIENT-BASIC-CONSULTATION"],
      },
      {
        key: "outpatient_referral_specialist",
        label: "Referral to Specialist",
        codes: ["OUTPATIENT-REFERRAL-TO-SPECIALIST"],
      },
      {
        key: "outpatient_laboratory",
        label: "OP Laboratory",
        codes: ["OUTPATIENT-LABORATORY"],
      },
      {
        key: "outpatient_opdor",
        label: "OPD/OR",
        codes: ["OUTPATIENT-OPD-OR"],
      },
      {
        key: "outpatient_facility_fee",
        label: "Facility Fee",
        codes: ["OUTPATIENT-FACILITY-FEE"],
      },
      {
        key: "outpatient_clinic_setting",
        label: "Clinic Setting",
        codes: ["OUTPATIENT-CLINIC-SETTING"],
      },
      {
        key: "outpatient_ataccess",
        label: "ATACCESS",
        codes: ["ATACCESS"],
      },
      {
        key: "outpatient_medpadala",
        label: "MEDPADALA",
        codes: ["MEDPADALA"],
      },
      {
        key: "outpatient_mpsyche",
        label: "MPSYCHE",
        codes: ["MPSYCHE"],
      },
    ],
  },
  {
    title: "APE",
    items: [
      {
        key: "ape_annual_physical_exam",
        label: "ANNUAL PHYSICAL EXAM (APE)",
        codes: ["APE"],
      },
    ],
  },
  {
    title: "EMERGENCY",
    items: [
      {
        key: "emergency",
        label: "EMERGENCY",
        codes: ["EMERGENCY"],
      },
    ],
  },
  {
    title: "MATERNITY",
    items: [
      {
        key: "maternity_pre_post_natal",
        label: "Pre and Post Natal Consultation",
        codes: ["MATERNITY-BASIC-CONSULTATION"],
      },
      {
        key: "maternity_laboratory",
        label: "Laboratory",
        codes: ["MATERNITY-LABORATORY"],
      },
      {
        key: "maternity_delivery",
        label: "Delivery",
        codes: ["MATERNITY-ADMISSION", "MATERNITY-DISCHARGE"],
      },
    ],
  },
  {
    title: "REIMBURSEMENT",
    items: [
      {
        key: "reimbursement_inpatient",
        label: "RI Inpatient",
        codes: ["RI-INPATIENT"],
      },
      {
        key: "reimbursement_outpatient",
        label: "RI Outpatient",
        codes: ["RI-OUTPATIENT"],
      },
    ],
  },
];

const TransactionDetailModal = ({ isOpen, row, sourceLabel, onClose }) => {
  if (!isOpen || !row) return null;

  const detailFields = [
    { label: "TRN No.", value: formatDetailText(row.trans_no) },
    { label: "LOA No.", value: formatDetailText(row.loa_no) },
    { label: "TRN Date", value: formatDate(row.trans_date, true, "N/A") },
    { label: "Member", value: formatDetailText(row.member_name) },
    { label: "Member ID", value: formatDetailText(row.member_id) },
    { label: "PhilHealth ID", value: formatDetailText(row.philhealth_id) },
    { label: "Provider", value: formatDetailText(row.provider_name) },
    { label: "Coverage", value: formatDetailText(row.coverage_code) },
    { label: "TRN Status", value: formatDetailText(row.trans_flag_desc) },
    { label: "Claim Status", value: formatDetailText(row.claim_status) },
    { label: "LOA Amount", value: formatCurrency(row.loa_amount) },
    { label: "Claims Amount", value: formatCurrency(row.claim_amount) },
    { label: "Amount", value: formatCurrency(row.amount) },
    { label: "Expiry Date", value: formatDate(row.expire_date, true, "N/A") },
    { label: "Chief Complaint", value: formatDetailText(row.chief_complaint) },
    { label: "ICD-10", value: formatDetailText(row.icd_10_col) },
    { label: "Diagnosis", value: formatDetailText(row.diagnosis_col) },
    { label: "ICD Category Code", value: formatDetailText(row.categ_code_col) },
    { label: "ICD Category", value: formatDetailText(row.categ_desc_col) },
  ];

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/60 p-4">
      <div className="w-full max-w-5xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_28px_80px_rgba(15,23,42,0.35)]">
        <div className="flex items-start justify-between border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white px-6 py-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">
              {sourceLabel}
            </p>
            <h3 className="mt-1 text-lg font-semibold text-slate-900">
              Transaction Details
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              TRN: {formatDetailText(row.trans_no)} | LOA:{" "}
              {formatDetailText(row.loa_no)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
            aria-label="Close transaction detail modal"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto px-6 py-5">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
            {detailFields.map((field) => (
              <div
                key={field.label}
                className="rounded-xl border border-slate-100 bg-slate-50/70 p-3"
              >
                <p className="text-[11px] font-semibold uppercase tracking-[0.05em] text-slate-500">
                  {field.label}
                </p>
                <p className="mt-1 break-words text-sm font-medium text-slate-800">
                  {field.value}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const MEMBER_VIEW_TABS = [
  "member-details",
  "plan-details",
  "member-transaction",
  "family-members",
  "family-transaction",
];

const normalizeMemberViewTab = (tab) => {
  const normalized = String(tab || "").trim().toLowerCase();
  return MEMBER_VIEW_TABS.includes(normalized)
    ? normalized
    : "member-details";
};

const toExportCellValue = (row, key) => {
  if (key === "trans_date") return formatDate(row?.trans_date, true);
  if (key === "loa_amount" || key === "claim_amount") {
    if (row?.[key] == null || row?.[key] === "") return "";
    const num = Number(row[key]);
    return Number.isNaN(num) ? row[key] : num;
  }
  return row?.[key] ?? "";
};

const toFamilyMemberExportCellValue = (row, key) => {
  if (key === "date_of_birth") return formatDate(row?.date_of_birth);
  if (key === "member_type") {
    return row?.member_type === "p" ? "Principal" : "Dependent";
  }
  if (key === "status") {
    return getStatusFlagMeta(
      row?.status_flag,
      row?.flag_desc || row?.status_flag_desc
    ).label;
  }
  return row?.[key] ?? "";
};

const transactionMatchesSearch = (row, searchText) => {
  if (!searchText) return true;

  const haystack = [
    row?.trans_no,
    row?.loa_no,
    row?.member_name,
    row?.provider_name,
    row?.coverage_code,
    row?.trans_flag_desc,
    row?.claim_status,
    row?.chief_complaint,
    row?.icd_10_col,
    row?.diagnosis_col,
    row?.categ_code_col,
    row?.categ_desc_col,
  ]
    .filter((value) => value != null && value !== "")
    .join(" ")
    .toLowerCase();

  return haystack.includes(searchText);
};

const exportTransactionsToCsv = (rows, fileName) => {
  const escapeCsvValue = (value) => {
    const stringValue = String(value ?? "");
    if (
      stringValue.includes('"') ||
      stringValue.includes(",") ||
      stringValue.includes("\n")
    ) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  };

  const headerLine = TRANSACTION_EXPORT_COLUMNS.map((column) =>
    escapeCsvValue(column.label)
  ).join(",");

  const rowLines = rows.map((row) =>
    TRANSACTION_EXPORT_COLUMNS.map((column) =>
      escapeCsvValue(toExportCellValue(row, column.key))
    ).join(",")
  );

  const csvContent = [headerLine, ...rowLines].join("\n");
  const blob = new Blob([csvContent], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const exportTransactionsToExcel = async (rows, fileName) => {
  const XLSX = await import("xlsx");
  const exportRows = rows.map((row) => {
    const exportRow = {};
    TRANSACTION_EXPORT_COLUMNS.forEach((column) => {
      exportRow[column.label] = toExportCellValue(row, column.key);
    });
    return exportRow;
  });

  const worksheet = XLSX.utils.json_to_sheet(exportRows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Transactions");
  XLSX.writeFile(workbook, fileName);
};

const exportFamilyMembersToCsv = (rows, fileName) => {
  const escapeCsvValue = (value) => {
    const stringValue = String(value ?? "");
    if (
      stringValue.includes('"') ||
      stringValue.includes(",") ||
      stringValue.includes("\n")
    ) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  };

  const headerLine = FAMILY_MEMBER_EXPORT_COLUMNS.map((column) =>
    escapeCsvValue(column.label)
  ).join(",");

  const rowLines = rows.map((row) =>
    FAMILY_MEMBER_EXPORT_COLUMNS.map((column) =>
      escapeCsvValue(toFamilyMemberExportCellValue(row, column.key))
    ).join(",")
  );

  const csvContent = [headerLine, ...rowLines].join("\n");
  const blob = new Blob([csvContent], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const exportFamilyMembersToExcel = async (rows, fileName) => {
  const XLSX = await import("xlsx");
  const exportRows = rows.map((row) => {
    const exportRow = {};
    FAMILY_MEMBER_EXPORT_COLUMNS.forEach((column) => {
      exportRow[column.label] = toFamilyMemberExportCellValue(row, column.key);
    });
    return exportRow;
  });

  const worksheet = XLSX.utils.json_to_sheet(exportRows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Family Members");
  XLSX.writeFile(workbook, fileName);
};

const MemberViewModal = ({
  memberId,
  onClose,
  variant = "modal",
  initialTab = "member-details",
}) => {
  const navigate = useNavigate();
  const isFullPage = variant === "fullpage";
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState(null);
  const [activeTab, setActiveTab] = useState(() =>
    normalizeMemberViewTab(initialTab)
  );
  const [isMaximized, setIsMaximized] = useState(false);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [planData, setPlanData] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [memberTransactions, setMemberTransactions] = useState([]);
  const [memberTransactionsLoading, setMemberTransactionsLoading] =
    useState(false);
  const [memberTransactionPeriod, setMemberTransactionPeriod] = useState("all");
  const [memberTransactionSearch, setMemberTransactionSearch] = useState("");
  const [memberTransactionCoverageFilter, setMemberTransactionCoverageFilter] =
    useState("all");
  const [memberTransactionStatusFilter, setMemberTransactionStatusFilter] =
    useState("all");
  const [memberTransactionCurrentPage, setMemberTransactionCurrentPage] =
    useState(1);
  const [memberTransactionItemsPerPage, setMemberTransactionItemsPerPage] =
    useState(10);
  const [familyTransactions, setFamilyTransactions] = useState([]);
  const [familyTransactionsLoading, setFamilyTransactionsLoading] =
    useState(false);
  const [familyTransactionPeriod, setFamilyTransactionPeriod] = useState("all");
  const [familyTransactionSearch, setFamilyTransactionSearch] = useState("");
  const [familyTransactionCoverageFilter, setFamilyTransactionCoverageFilter] =
    useState("all");
  const [familyTransactionStatusFilter, setFamilyTransactionStatusFilter] =
    useState("all");
  const [familyTransactionCurrentPage, setFamilyTransactionCurrentPage] =
    useState(1);
  const [familyTransactionItemsPerPage, setFamilyTransactionItemsPerPage] =
    useState(10);
  const [isTransactionDetailOpen, setIsTransactionDetailOpen] = useState(false);
  const [selectedTransactionRow, setSelectedTransactionRow] = useState(null);
  const [selectedTransactionSource, setSelectedTransactionSource] =
    useState("Transaction");

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
  });

  const fetchMemberTransactions = async () => {
    setMemberTransactionsLoading(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/members/view/member-transactions/${memberId}`,
        {
          params: { period: memberTransactionPeriod },
          headers: getAuthHeaders(),
        }
      );
      setMemberTransactions(response.data?.data || []);
    } catch (error) {
      toast.error(
        "Failed to fetch member transactions: " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setMemberTransactionsLoading(false);
    }
  };

  const fetchFamilyTransactions = async () => {
    setFamilyTransactionsLoading(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/members/view/family-transactions/${memberId}`,
        {
          params: { period: familyTransactionPeriod },
          headers: getAuthHeaders(),
        }
      );
      setFamilyTransactions(response.data?.data || []);
    } catch (error) {
      toast.error(
        "Failed to fetch family transactions: " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setFamilyTransactionsLoading(false);
    }
  };

  const handleRegenerateSobUrl = async (uploadId) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/members/view/regenerate-sob-url/${uploadId}`,
        {},
        { headers: getAuthHeaders() }
      );
      if (response.data.status === "success") {
        setPlanData((prev) => ({
          ...prev,
          sob_file_links: {
            ...prev.sob_file_links,
            version2: {
              url: response.data.signed_url,
              expires_at: response.data.expires_at,
              upload_id: uploadId,
            },
          },
        }));
        toast.success("SOB file URL regenerated successfully!");
      } else {
        toast.error(
          response.data.message || "Failed to regenerate SOB file URL."
        );
      }
    } catch (error) {
      toast.error(
        "Failed to regenerate SOB file URL: " +
          (error.response?.data?.message || error.message)
      );
    }
  };

  useEffect(() => {
    const fetchMember = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(
          `${BASE_URL}/members/view/details/${memberId}`,
          {
            headers: getAuthHeaders(),
          }
        );
        setFormData(response.data.data);
      } catch (error) {
        toast.error(
          "Failed to fetch member details: " +
            (error.response?.data?.message || error.message)
        );
        handleClose();
      } finally {
        setIsLoading(false);
      }
    };

    const fetchFamilyMembers = async () => {
      try {
        const response = await axios.get(
          `${BASE_URL}/members/view/family-members/${memberId}`,
          {
            headers: getAuthHeaders(),
          }
        );
        setFamilyMembers(response.data.members);
      } catch (error) {
        toast.error(
          "Failed to fetch family members: " +
            (error.response?.data?.message || error.message)
        );
      }
    };

    const fetchPlanDetails = async () => {
      if (formData?.plan_code) {
        try {
          const response = await axios.get(
            `${BASE_URL}/members/view/plans/${formData.plan_code}`,
            {
              headers: getAuthHeaders(),
            }
          );
          setPlanData(response.data);
        } catch (error) {
          toast.error(
            "Failed to fetch plan details: " +
              (error.response?.data?.message || error.message)
          );
        }
      }
    };

    fetchMember();
    fetchFamilyMembers();
    fetchPlanDetails();
  }, [memberId, formData?.plan_code]);

  useEffect(() => {
    if (activeTab === "member-transaction") {
      fetchMemberTransactions();
    }
  }, [activeTab, memberId, memberTransactionPeriod]);

  useEffect(() => {
    if (activeTab === "family-transaction") {
      fetchFamilyTransactions();
    }
  }, [activeTab, memberId, familyTransactionPeriod]);

  useEffect(() => {
    setActiveTab(normalizeMemberViewTab(initialTab));
  }, [initialTab]);

  const fullName = formData
    ? `${formData.last_name}, ${formData.first_name} ${
        formData.middle_name ? formData.middle_name : ""
      } ${formData.suffix ? formData.suffix : ""}`.trim()
    : "—";

  const fullAddress = formData
    ? [
        formData.streetno_bldg,
        formData.baranggay,
        formData.towncity,
        formData.province,
        formData.region,
        formData.postalcode,
      ]
        .filter(Boolean)
        .join(", ")
    : "—";

  const buildPlanEditPath = (corpCode, planCode) => {
    const normalizedCorpCode = String(corpCode ?? "").trim();
    const normalizedPlanCode = String(planCode ?? "").trim();
    if (!normalizedCorpCode || !normalizedPlanCode) {
      return null;
    }

    return `/plan-and-coverage-benefits-setup/edit/${normalizedCorpCode}/${normalizedPlanCode}`;
  };

  const planEditPath = buildPlanEditPath(formData?.corp_code, formData?.plan_code);

  const getPlanLinkValue = (displayValue) => {
    if (!displayValue) return null;
    if (!planEditPath) return displayValue;

    return (
      <button
        type="button"
        onClick={() => navigate(planEditPath)}
        className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 hover:underline"
      >
        {displayValue}
        <ArrowTopRightOnSquareIcon className="h-4 w-4" />
      </button>
    );
  };

  const getFamilyMemberPlanLinkValue = (member, displayValue) => {
    const normalizedValue = String(displayValue ?? "").trim();
    if (!normalizedValue) return "—";

    const familyMemberPlanPath = buildPlanEditPath(
      member?.corp_code || formData?.corp_code,
      member?.plan_code
    );
    if (!familyMemberPlanPath) {
      return normalizedValue;
    }

    return (
      <button
        type="button"
        onClick={() => navigate(familyMemberPlanPath)}
        className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 hover:underline"
      >
        {normalizedValue}
        <ArrowTopRightOnSquareIcon className="h-4 w-4" />
      </button>
    );
  };

  const planLimitTypes = [
    { value: "1", label: "MBL Only (Maximum Benefit Limit)" },
    { value: "2", label: "ABL Only (Annual Benefit Limit)" },
    { value: "3", label: "Both MBL & ABL" },
  ];

  const coverageDisplaySections = useMemo(() => {
    const sourceRows = Array.isArray(planData?.coverages)
      ? planData.coverages
      : [];

    const coverageByCode = new Map();
    sourceRows.forEach((coverage) => {
      const normalizedCode = normalizeCoverageCode(coverage?.coverage_code);
      if (!normalizedCode || coverageByCode.has(normalizedCode)) {
        return;
      }
      coverageByCode.set(normalizedCode, coverage);
    });

    const configuredCodeSet = new Set();
    const configuredSections = PLAN_COVERAGE_DISPLAY_CONFIG.map((section) => ({
      title: section.title,
      items: section.items.map((item) => {
        const normalizedCodes = item.codes
          .map((code) => normalizeCoverageCode(code))
          .filter(Boolean);

        normalizedCodes.forEach((code) => configuredCodeSet.add(code));

        const matchedCoverages = normalizedCodes
          .map((code) => coverageByCode.get(code))
          .filter(Boolean);

        return {
          key: item.key,
          label: item.label,
          checked: matchedCoverages.length > 0,
          expectedCodes: normalizedCodes,
        };
      }),
    }));

    const additionalItems = Array.from(coverageByCode.values())
      .filter(
        (coverage) =>
          !configuredCodeSet.has(normalizeCoverageCode(coverage?.coverage_code))
      )
      .sort((a, b) => {
        const aLabel = String(a.coverage_desc || a.coverage_code || "");
        const bLabel = String(b.coverage_desc || b.coverage_code || "");
        return aLabel.localeCompare(bLabel);
      })
      .map((coverage) => {
        const normalizedCode = normalizeCoverageCode(coverage?.coverage_code);
        const displayLabel = String(
          coverage?.coverage_desc || coverage?.coverage_code || normalizedCode
        ).trim();

        return {
          key: `additional_${normalizedCode}`,
          label:
            displayLabel === normalizedCode
              ? normalizedCode
              : `${displayLabel} (${normalizedCode})`,
          checked: true,
          expectedCodes: [normalizedCode],
        };
      });

    if (additionalItems.length > 0) {
      configuredSections.push({
        title: "ADDITIONAL COVERAGES",
        items: additionalItems,
      });
    }

    return configuredSections.filter((section) => section.items.length > 0);
  }, [planData?.coverages]);

  const benefitMap = {
    ben_lmt_ip_max_limit: { coverage: "INPATIENT", code: "ip_max_limit_basic" },
    ben_lmt_ip_room_n_board: { coverage: "INPATIENT", code: "LMKIUWSQ64" },
    ben_lmt_ip_room_n_board_cat: { coverage: "INPATIENT", code: "LMKIUWSQ64" },
    ben_lmt_ip_misc_hospital_services: {
      coverage: "INPATIENT",
      code: "VLJETCUQOC",
    },
    ben_lmt_ip_surgical_fee: { coverage: "INPATIENT", code: "WQTJXUBHZR" },
    ben_lmt_ip_physician_fee: { coverage: "INPATIENT", code: "BXAENWLC89" },
    ben_lmt_ip_opdor: { coverage: "INPATIENT", code: "KEKMDSWADP" },
    ben_lmt_ip_ecu: { coverage: "INPATIENT", code: "TB71MTN9K0" },
    ben_lmt_ip_surgical_fee_anes_fee: {
      coverage: "INPATIENT",
      code: "WXLZFNRUBH",
    },
    ben_lmt_op_max_limit: {
      coverage: "OUTPATIENT",
      code: "op_max_limit_basic",
    },
    ben_lmt_op_basic_consultation: {
      coverage: "OUTPATIENT",
      code: "MKMG7IA5AG",
    },
    ben_lmt_op_laboratory: { coverage: "OUTPATIENT", code: "EGQIHVQB58" },
    ben_lmt_op_clinic_setting: { coverage: "OUTPATIENT", code: "HYTJ8K1JTI" },
    ben_lmt_op_emergency: { coverage: "OUTPATIENT", code: "CFDEASKQ19" },
    ben_lmt_op_medicine: { coverage: "OUTPATIENT", code: "QAFRMMWNEL" },
    ben_lmt_op_ataccess: { coverage: "ATACCESS", code: "YL440L5KBY" },
    ben_lmt_op_medpadala: { coverage: "MEDPADALA", code: "WTIGQLQA25" },
    ben_lmt_op_mpsyche: { coverage: "MPSYCHE", code: "MPSYCHE" },
    ben_lmt_mat_max_limit: {
      coverage: "MATERNITY",
      code: "mat_max_limit_basic",
    },
    ben_lmt_mat_normal_delivery: { coverage: "MATERNITY", code: "K20I8CQQ9T" },
    ben_lmt_mat_cs_delivery: { coverage: "MATERNITY", code: "AAM69QNWLP" },
    ben_lmt_mat_pre_natal: { coverage: "MATERNITY", code: "QFT8DSWXOL" },
    ben_lmt_mat_post_natal: { coverage: "MATERNITY", code: "MWS9XG8X04" },
    ben_lmt_ape_max_limit: { coverage: "APE", code: "ape_max_limit_basic" },
  };

  // Family Members Pagination and Filtering
  const filteredMembers = useMemo(() => {
    return familyMembers.filter((member) =>
      `${member.member_id} ${member.last_name} ${member.first_name} ${
        member.middle_name || ""
      }`
        .toLowerCase()
        .includes(searchQuery.toLowerCase())
    );
  }, [familyMembers, searchQuery]);

  const totalItems = filteredMembers.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const paginatedMembers = useMemo(() => {
    return filteredMembers.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage
    );
  }, [filteredMembers, currentPage, itemsPerPage]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const memberTransactionCoverageOptions = useMemo(() => {
    return [...new Set(memberTransactions.map((row) => row.coverage_code))]
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
  }, [memberTransactions]);

  const memberTransactionStatusOptions = useMemo(() => {
    return [...new Set(memberTransactions.map((row) => row.trans_flag_desc))]
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
  }, [memberTransactions]);

  const filteredMemberTransactions = useMemo(() => {
    const searchText = memberTransactionSearch.trim().toLowerCase();
    return memberTransactions.filter((row) => {
      const coverageMatches =
        memberTransactionCoverageFilter === "all" ||
        (row.coverage_code || "") === memberTransactionCoverageFilter;
      const statusMatches =
        memberTransactionStatusFilter === "all" ||
        (row.trans_flag_desc || "") === memberTransactionStatusFilter;
      const searchMatches = transactionMatchesSearch(row, searchText);
      return coverageMatches && statusMatches && searchMatches;
    });
  }, [
    memberTransactions,
    memberTransactionSearch,
    memberTransactionCoverageFilter,
    memberTransactionStatusFilter,
  ]);

  const memberTransactionTotalItems = filteredMemberTransactions.length;
  const memberTransactionTotalPages = Math.max(
    1,
    Math.ceil(memberTransactionTotalItems / memberTransactionItemsPerPage)
  );
  const paginatedMemberTransactions = useMemo(() => {
    return filteredMemberTransactions.slice(
      (memberTransactionCurrentPage - 1) * memberTransactionItemsPerPage,
      memberTransactionCurrentPage * memberTransactionItemsPerPage
    );
  }, [
    filteredMemberTransactions,
    memberTransactionCurrentPage,
    memberTransactionItemsPerPage,
  ]);

  const handleMemberTransactionPageChange = (page) => {
    if (page >= 1 && page <= memberTransactionTotalPages) {
      setMemberTransactionCurrentPage(page);
    }
  };

  const familyTransactionCoverageOptions = useMemo(() => {
    return [...new Set(familyTransactions.map((row) => row.coverage_code))]
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
  }, [familyTransactions]);

  const familyTransactionStatusOptions = useMemo(() => {
    return [...new Set(familyTransactions.map((row) => row.trans_flag_desc))]
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
  }, [familyTransactions]);

  const filteredFamilyTransactions = useMemo(() => {
    const searchText = familyTransactionSearch.trim().toLowerCase();
    return familyTransactions.filter((row) => {
      const coverageMatches =
        familyTransactionCoverageFilter === "all" ||
        (row.coverage_code || "") === familyTransactionCoverageFilter;
      const statusMatches =
        familyTransactionStatusFilter === "all" ||
        (row.trans_flag_desc || "") === familyTransactionStatusFilter;
      const searchMatches = transactionMatchesSearch(row, searchText);
      return coverageMatches && statusMatches && searchMatches;
    });
  }, [
    familyTransactions,
    familyTransactionSearch,
    familyTransactionCoverageFilter,
    familyTransactionStatusFilter,
  ]);

  const familyTransactionTotalItems = filteredFamilyTransactions.length;
  const familyTransactionTotalPages = Math.max(
    1,
    Math.ceil(familyTransactionTotalItems / familyTransactionItemsPerPage)
  );
  const paginatedFamilyTransactions = useMemo(() => {
    return filteredFamilyTransactions.slice(
      (familyTransactionCurrentPage - 1) * familyTransactionItemsPerPage,
      familyTransactionCurrentPage * familyTransactionItemsPerPage
    );
  }, [
    filteredFamilyTransactions,
    familyTransactionCurrentPage,
    familyTransactionItemsPerPage,
  ]);

  const handleFamilyTransactionPageChange = (page) => {
    if (page >= 1 && page <= familyTransactionTotalPages) {
      setFamilyTransactionCurrentPage(page);
    }
  };

  useEffect(() => {
    setMemberTransactionCurrentPage(1);
  }, [
    memberTransactionPeriod,
    memberTransactionSearch,
    memberTransactionCoverageFilter,
    memberTransactionStatusFilter,
    memberTransactionItemsPerPage,
  ]);

  useEffect(() => {
    setFamilyTransactionCurrentPage(1);
  }, [
    familyTransactionPeriod,
    familyTransactionSearch,
    familyTransactionCoverageFilter,
    familyTransactionStatusFilter,
    familyTransactionItemsPerPage,
  ]);

  useEffect(() => {
    if (memberTransactionCurrentPage > memberTransactionTotalPages) {
      setMemberTransactionCurrentPage(memberTransactionTotalPages);
    }
  }, [memberTransactionCurrentPage, memberTransactionTotalPages]);

  useEffect(() => {
    if (familyTransactionCurrentPage > familyTransactionTotalPages) {
      setFamilyTransactionCurrentPage(familyTransactionTotalPages);
    }
  }, [familyTransactionCurrentPage, familyTransactionTotalPages]);

  const handleExportMemberTransactionsCsv = () => {
    if (!filteredMemberTransactions.length) {
      toast.info("No member transactions to export.");
      return;
    }

    const fileName = `member_transaction_history_${memberId}_${dayjs().format(
      "YYYYMMDD_HHmmss"
    )}.csv`;
    exportTransactionsToCsv(filteredMemberTransactions, fileName);
  };

  const handleExportMemberTransactionsExcel = async () => {
    if (!filteredMemberTransactions.length) {
      toast.info("No member transactions to export.");
      return;
    }

    try {
      const fileName = `member_transaction_history_${memberId}_${dayjs().format(
        "YYYYMMDD_HHmmss"
      )}.xlsx`;
      await exportTransactionsToExcel(filteredMemberTransactions, fileName);
    } catch (error) {
      toast.error(`Failed to export Excel: ${error.message}`);
    }
  };

  const handleExportFamilyTransactionsCsv = () => {
    if (!filteredFamilyTransactions.length) {
      toast.info("No family transactions to export.");
      return;
    }

    const fileName = `family_transaction_history_${memberId}_${dayjs().format(
      "YYYYMMDD_HHmmss"
    )}.csv`;
    exportTransactionsToCsv(filteredFamilyTransactions, fileName);
  };

  const handleExportFamilyTransactionsExcel = async () => {
    if (!filteredFamilyTransactions.length) {
      toast.info("No family transactions to export.");
      return;
    }

    try {
      const fileName = `family_transaction_history_${memberId}_${dayjs().format(
        "YYYYMMDD_HHmmss"
      )}.xlsx`;
      await exportTransactionsToExcel(filteredFamilyTransactions, fileName);
    } catch (error) {
      toast.error(`Failed to export Excel: ${error.message}`);
    }
  };

  const handleExportFamilyMembersCsv = () => {
    if (!filteredMembers.length) {
      toast.info("No family members to export.");
      return;
    }

    const fileName = `family_members_${memberId}_${dayjs().format(
      "YYYYMMDD_HHmmss"
    )}.csv`;
    exportFamilyMembersToCsv(filteredMembers, fileName);
  };

  const handleExportFamilyMembersExcel = async () => {
    if (!filteredMembers.length) {
      toast.info("No family members to export.");
      return;
    }

    try {
      const fileName = `family_members_${memberId}_${dayjs().format(
        "YYYYMMDD_HHmmss"
      )}.xlsx`;
      await exportFamilyMembersToExcel(filteredMembers, fileName);
    } catch (error) {
      toast.error(`Failed to export Excel: ${error.message}`);
    }
  };

  const handleClose = () => {
    setIsTransactionDetailOpen(false);
    setSelectedTransactionRow(null);
    setSelectedTransactionSource("Transaction");

    if (isFullPage) {
      window.close();
      return;
    }

    if (onClose) {
      onClose();
    }
  };

  const handleOpenFullPage = () => {
    const params = new URLSearchParams({
      member_id: memberId || "",
      tab: normalizeMemberViewTab(activeTab),
    });
    window.open(`/members/view/full-page?${params.toString()}`, "_blank");
  };

  const openTransactionDetails = (row, sourceLabel) => {
    setSelectedTransactionRow(row || null);
    setSelectedTransactionSource(sourceLabel || "Transaction");
    setIsTransactionDetailOpen(true);
  };

  const closeTransactionDetails = () => {
    setIsTransactionDetailOpen(false);
    setSelectedTransactionRow(null);
    setSelectedTransactionSource("Transaction");
  };

  const handleResendWelcomeAlert = async () => {
    const result = await Swal.fire({
      title: "Resend Member's Text Welcome Alert",
      html: `
      <div class="text-left">
        <p><strong>Member:</strong> ${fullName}</p>
        <p><strong>Mobile#:</strong> ${formData?.mob_tel || "N/A"}</p>
        <p><strong>Email#:</strong> ${formData?.email_add || "N/A"}</p>
      </div>
      <p>Are you sure you want to resend welcome text alert?</p>
    `,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "Cancel",
      buttonsStyling: false,
      customClass: {
        confirmButton:
          "bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 mr-2",
        cancelButton:
          "bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300",
      },
    });

    if (result.isConfirmed) {
      const modeResult = await Swal.fire({
        title: "Resend Welcome Alert via",
        showCancelButton: true,
        cancelButtonText: "Cancel",
        showDenyButton: true,
        confirmButtonText: "Email",
        denyButtonText: "SMS",
        buttonsStyling: false,
        customClass: {
          confirmButton:
            "bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 mr-2",
          denyButton:
            "bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 mr-2",
          cancelButton:
            "bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300",
        },
      });

      if (modeResult.isConfirmed || modeResult.isDenied) {
        const mode = modeResult.isConfirmed ? "email" : "sms";
        try {
          const response = await axios.post(
            `${BASE_URL}/members/resend-welcome-alert/${memberId}`,
            { mode },
            { headers: getAuthHeaders() }
          );
          if (response.data.status === "success") {
            toast.success(response.data.message);
          } else {
            toast.error(
              response.data.message || `Failed to send ${mode} alert.`
            );
          }
        } catch (error) {
          toast.error(
            `Failed to send ${mode} alert: ` +
              (error.response?.data?.message || error.message)
          );
        }
      }
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className={clsx(
          "z-50",
          isFullPage
            ? "min-h-screen bg-slate-100"
            : "fixed inset-0 flex items-center justify-center bg-black bg-opacity-60"
        )}
      >
        <motion.div
          initial={{ scale: 0.95, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.95, y: 20 }}
          className={clsx(
            "bg-white shadow-2xl overflow-auto p-6",
            isFullPage
              ? "w-full min-h-screen max-h-none rounded-none"
              : isMaximized
              ? "w-[95vw] h-[95vh] rounded-xl"
              : "w-full max-w-6xl max-h-[90vh] rounded-xl"
          )}
        >
          <div className="flex items-center justify-between border-b border-gray-100 pb-3">
            <div className="flex items-center gap-2">
              <button
                onClick={handleClose}
                className="rounded-md p-1.5 text-gray-600 hover:bg-gray-100"
              >
                <ArrowLeftIcon className="h-5 w-5" />
              </button>
              <h2 className="text-lg font-semibold text-gray-900">
                Member Details{" "}
                <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-sm font-medium text-blue-700">
                  {fullName} - {memberId}
                </span>
              </h2>
            </div>
            <div className="flex items-center gap-2">
              {!isFullPage && (
                <button
                  type="button"
                  onClick={handleOpenFullPage}
                  className="relative inline-flex items-center gap-1.5 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-700 transition-colors hover:bg-indigo-100"
                >
                  <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                  View in Full Page
                  <span className="absolute -right-2 -top-2 inline-flex items-center rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white shadow-sm">
                    New
                  </span>
                </button>
              )}

              {!isFullPage && (
                <button
                  type="button"
                  onClick={() => setIsMaximized((prev) => !prev)}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-200"
                >
                  {isMaximized ? (
                    <ArrowsPointingInIcon className="h-4 w-4" />
                  ) : (
                    <ArrowsPointingOutIcon className="h-4 w-4" />
                  )}
                  {isMaximized ? "Restore" : "Maximize"}
                </button>
              )}

              <button
                type="button"
                onClick={handleClose}
                className="rounded-md p-1.5 text-gray-600 hover:bg-gray-100"
                aria-label="Close member view"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>

          {isLoading ? (
            <div className="mt-6">
              <SkeletonBlock />
            </div>
          ) : (
            <div className="mt-6">
              <div className="sticky top-0 z-30 -mx-6 bg-white/95 px-6 pt-1 backdrop-blur supports-[backdrop-filter]:bg-white/90">
                <div className="flex border-b border-gray-100">
                  <TabButton
                    active={activeTab === "member-details"}
                    onClick={() => setActiveTab("member-details")}
                    label="Member Details"
                    Icon={UserIcon}
                  />
                  <TabButton
                    active={activeTab === "plan-details"}
                    onClick={() => setActiveTab("plan-details")}
                    label="Plan Details"
                    Icon={ShieldCheckIcon}
                  />
                  <TabButton
                    active={activeTab === "member-transaction"}
                    onClick={() => setActiveTab("member-transaction")}
                    label="Member Transaction History"
                    Icon={ClockIcon}
                  />
                  <TabButton
                    active={activeTab === "family-members"}
                    onClick={() => setActiveTab("family-members")}
                    label="Family Members"
                    Icon={UserGroupIcon}
                  />
                  <TabButton
                    active={activeTab === "family-transaction"}
                    onClick={() => setActiveTab("family-transaction")}
                    label="Family Transaction History"
                    Icon={ClockIcon}
                  />
                </div>
              </div>

              <div className="mt-4">
              
                {activeTab === "member-details" && (
                  <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                    <div className="col-span-1 md:col-span-2">
                      <div className="rounded-1xl border-l-4 border-amber-400 bg-amber-50 p-5 shadow-md relative">
                        <div className="absolute top-3 right-3 text-amber-500">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                        </div>
                        <h3 className="text-sm font-bold text-amber-700 uppercase tracking-wide mb-2">
                          Special Instructions
                        </h3>
                        <p className="text-gray-800 text-base leading-relaxed">
                          {formData?.special_instructions || "—"}
                        </p>
                      </div>
                    </div>

                    <div>
                      <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                        <h3 className="text-base font-semibold text-gray-900 mb-3">
                          Basic Information
                        </h3>
                        <InfoRow
                          label="Member Name"
                          value={fullName}
                          copyable
                        />
                        <InfoRow
                          label="Member ID"
                          value={formData?.member_id}
                          copyable
                        />
                        <InfoRow
                          label="VIP Tag"
                          value={
                            formData?.vip_tag ? (
                              <Badge color="green">Yes</Badge>
                            ) : (
                              <Badge color="red">No</Badge>
                            )
                          }
                        />
                        <InfoRow
                          label="Plan Code"
                          value={getPlanLinkValue(formData?.plan_code)}
                        />
                        <InfoRow
                          label="Plan Name"
                          value={getPlanLinkValue(formData?.plan_name)}
                        />
                        <InfoRow
                          label="Member Type"
                          value={
                            formData?.member_type === "p"
                              ? "Principal"
                              : "Dependent"
                          }
                        />
                        <InfoRow
                          label="Corporate"
                          value={formData?.corp_name}
                        />
                        <InfoRow
                          label="Corporate Name in Card"
                          value={formData?.corp_card_name}
                        />
                        <InfoRow
                          label="Corporate ID"
                          value={formData?.corp_code}
                          copyable
                        />
                        <InfoRow
                          label="Employee ID"
                          value={formData?.employee_id}
                          copyable
                        />
                        <InfoRow label="Address" value={fullAddress} />
                        <InfoRow
                          label="Enrolled By"
                          value={formData?.enrolled_by}
                        />
                      </div>
                    </div>

                    <div>
                      <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                        <h3 className="text-base font-semibold text-gray-900 mb-3">
                          Account Details
                        </h3>
                        <InfoRow
                          label="Account Status"
                          value={renderStatusFlagBadge(
                            formData?.status_flag,
                            formData?.flag_desc || formData?.status_flag_desc
                          )}
                        />
                        <InfoRow
                          label="Username"
                          value={formData?.member_username}
                          copyable
                        />
                        <InfoRow
                          label="Date Enrolled"
                          value={formatDate(formData?.enrollment_date)}
                        />
                        <InfoRow
                          label="Activation Date"
                          value={formatDate(formData?.activation_date)}
                        />
                        <InfoRow
                          label="Termination Date"
                          value={formatDate(formData?.termination_date)}
                        />
                        <InfoRow
                          label="Suspension Date"
                          value={formatDate(formData?.suspension_date)}
                        />
                        <InfoRow
                          label="Reactivation Date"
                          value={formatDate(formData?.reactivation_date)}
                        />
                        <InfoRow
                          label="Renewal Date"
                          value={formatDate(formData?.renewal_date)}
                        />
                        <InfoRow
                          label="Expiry Date"
                          value={formatDate(formData?.expiry_date)}
                        />
                      </div>
                    </div>

                    <div>
                      <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                        <h3 className="text-base font-semibold text-gray-900 mb-3">
                          Other Details
                        </h3>
                        <InfoRow
                          label="Principal Full Name"
                          value={
                            formData?.principal_id
                              ? `${formData?.principal_last_name}, ${
                                  formData?.principal_first_name
                                } ${
                                  formData?.principal_middle_name || ""
                                }`.trim()
                              : "—"
                          }
                        />
                        <InfoRow
                          label="Employment Date"
                          value={formatDate(formData?.employment_date)}
                        />
                        <InfoRow
                          label="Department"
                          value={formData?.department}
                        />
                        <InfoRow label="Position" value={formData?.position} />
                        <InfoRow
                          label="Designation"
                          value={formData?.designation}
                        />
                        <InfoRow
                          label="Date Hired"
                          value={formatDate(formData?.hired_date)}
                        />
                        <InfoRow
                          label="Philhealth Number"
                          value={formData?.philhealth_id}
                          copyable
                        />
                        <InfoRow
                          label="TIN Number"
                          value={formData?.tin_no}
                          copyable
                        />
                        <InfoRow
                          label="Email Address"
                          value={formData?.email_add}
                          copyable
                        />
                        <InfoRow
                          label="Telephone Number"
                          value={formData?.tel_no}
                          copyable
                        />
                        <InfoRow
                          label="Mobile Number"
                          value={formData?.mob_tel}
                          copyable
                        />
                      </div>
                    </div>

                    <div>
                      <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                        <h3 className="text-base font-semibold text-gray-900 mb-3">
                          Additional Details
                        </h3>
                        <InfoRow
                          label="SSS Number"
                          value={formData?.sss_no}
                          copyable
                        />
                        <InfoRow
                          label="Passport Number"
                          value={formData?.passport_no}
                          copyable
                        />
                        <InfoRow
                          label="Policy Number"
                          value={formData?.policy_no}
                          copyable
                        />
                        <InfoRow
                          label="Bank Name"
                          value={formData?.bank_name}
                        />
                        <InfoRow
                          label="Bank Branch Name"
                          value={formData?.bank_branch}
                        />
                        <InfoRow
                          label="Bank Account Number"
                          value={formData?.bank_account_no}
                          copyable
                        />
                        <InfoRow label="Nickname" value={formData?.nickname} />
                        <InfoRow
                          label="Gender"
                          value={getDisplayGender(formData?.gender)}
                        />
                        <InfoRow
                          label="Civil Status"
                          value={getDisplayCivilStatus(formData?.civil_status)}
                        />
                        <InfoRow
                          label="Nationality"
                          value={formData?.nationality}
                        />
                        <InfoRow
                          label="Date of Birth"
                          value={formatDate(formData?.date_of_birth)}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "plan-details" && (
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <div className="col-span-1 md:col-span-2">
                      <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                        <h3 className="text-base font-semibold text-gray-900 mb-3">
                          Information
                        </h3>
                        <InfoRow
                          label="Plan Limit Type"
                          value={
                            planLimitTypes.find(
                              (type) =>
                                type.value == planData?.plan?.plan_limit_type
                            )?.label || "—"
                          }
                        />
                        <InfoRow
                          label="SOB File Link (From AllAccess System v1)"
                          value={
                            planData?.sob_file_links?.version1 ? (
                              <a
                                href={planData.sob_file_links.version1}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline"
                              >
                                Download SOB File (Version 1)
                              </a>
                            ) : (
                              "—"
                            )
                          }
                        />
                        <InfoRow
                          label="SOB File Link (From AllAccess System v2.0)"
                          value={
                            planData?.sob_file_links?.version2?.url ? (
                              new Date(
                                planData.sob_file_links.version2.expires_at
                              ) < new Date() ? (
                                <div className="flex items-center gap-2">
                                  <span className="text-red-600">Expired</span>
                                  <button
                                    onClick={() =>
                                      handleRegenerateSobUrl(
                                        planData.sob_file_links.version2
                                          .upload_id
                                      )
                                    }
                                    className="text-blue-600 hover:underline"
                                  >
                                    Regenerate
                                  </button>
                                </div>
                              ) : (
                                <a
                                  href={planData.sob_file_links.version2.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:underline"
                                >
                                  Download SOB File (Version 2)
                                </a>
                              )
                            ) : (
                              "—"
                            )
                          }
                        />
                      </div>
                    </div>

                    <div>
                      <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                        <h3 className="text-base font-semibold text-gray-900 mb-3">
                          Coverage
                        </h3>
                        {planData && coverageDisplaySections.length > 0 ? (
                          <div className="space-y-4">
                            {coverageDisplaySections.map((section) => (
                              <div
                                key={section.title}
                                className="border-b border-gray-200 pb-4"
                              >
                                <h4 className="text-sm font-semibold text-gray-900 mb-2">
                                  {section.title}
                                </h4>
                                <div className="grid grid-cols-2 gap-4">
                                  {section.items.map((item) => {
                                    return (
                                      <div
                                        key={item.key}
                                        className="flex items-center gap-2"
                                      >
                                        <input
                                          type="checkbox"
                                          checked={item.checked}
                                          disabled
                                          className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <div className="flex flex-col">
                                          <span className="text-sm text-gray-700">
                                            {item.label}
                                          </span>
                                          {item.expectedCodes?.length > 0 && (
                                            <span className="text-xs text-gray-500">
                                              {item.expectedCodes.join(", ")}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <EmptyState
                            title="No coverage data"
                            subtitle="No coverage information available for this plan."
                          />
                        )}
                      </div>
                    </div>

                    <div>
                      <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                        <h3 className="text-base font-semibold text-gray-900 mb-3">
                          Benefit Limit
                        </h3>
                        {planData?.benefits?.length > 0 ||
                        (planData?.plan?.no_of_visits != null &&
                          planData.plan.no_of_visits !== "") ? (
                          <div className="space-y-4">
                            {[
                              {
                                title: "Inpatient",
                                fields: [
                                  {
                                    label: "Max Limit",
                                    name: "ben_lmt_ip_max_limit",
                                  },
                                  {
                                    label: "Room & Board (Peso Based)",
                                    name: "ben_lmt_ip_room_n_board",
                                  },
                                  {
                                    label: "Room & Board (Category)",
                                    name: "ben_lmt_ip_room_n_board_cat",
                                  },
                                  {
                                    label: "Misc Hospital Services",
                                    name: "ben_lmt_ip_misc_hospital_services",
                                  },
                                  {
                                    label: "Surgical Fee",
                                    name: "ben_lmt_ip_surgical_fee",
                                  },
                                  {
                                    label: "Physician Fee",
                                    name: "ben_lmt_ip_physician_fee",
                                  },
                                  { label: "OPD/OR", name: "ben_lmt_ip_opdor" },
                                  { label: "ECU", name: "ben_lmt_ip_ecu" },
                                  {
                                    label: "Surgical Fee Anes",
                                    name: "ben_lmt_ip_surgical_fee_anes_fee",
                                  },
                                ],
                              },
                              {
                                title: "Outpatient",
                                fields: [
                                  {
                                    label: "Max Limit",
                                    name: "ben_lmt_op_max_limit",
                                  },
                                  {
                                    label: "Total Number of Visit",
                                    name: "ben_lmt_op_total_no_visit_basic",
                                    planField: "no_of_visits",
                                    single: true,
                                  },
                                  {
                                    label: "Basic Consultation",
                                    name: "ben_lmt_op_basic_consultation",
                                  },
                                  {
                                    label: "Laboratory",
                                    name: "ben_lmt_op_laboratory",
                                  },
                                  {
                                    label: "Clinic Setting",
                                    name: "ben_lmt_op_clinic_setting",
                                  },
                                  {
                                    label: "Emergency",
                                    name: "ben_lmt_op_emergency",
                                  },
                                  {
                                    label: "Medicine",
                                    name: "ben_lmt_op_medicine",
                                  },
                                  {
                                    label: "AtAccess",
                                    name: "ben_lmt_op_ataccess",
                                  },
                                  {
                                    label: "MedPadala",
                                    name: "ben_lmt_op_medpadala",
                                  },
                                  {
                                    label: "MPsyche",
                                    name: "ben_lmt_op_mpsyche",
                                  },
                                ],
                              },
                              {
                                title: "Maternity",
                                fields: [
                                  {
                                    label: "Max Limit",
                                    name: "ben_lmt_mat_max_limit",
                                  },
                                  {
                                    label: "Normal/CS Delivery",
                                    name: "ben_lmt_mat_normal_delivery",
                                  },
                                  {
                                    label: "Pre & Post Natal",
                                    name: "ben_lmt_mat_pre_natal",
                                  },
                                ],
                              },
                              {
                                title: "APE",
                                fields: [
                                  {
                                    label: "Max Limit",
                                    name: "ben_lmt_ape_max_limit",
                                  },
                                ],
                              },
                            ].map((section) => (
                              <div
                                key={section.title}
                                className="border-b border-gray-200 pb-4"
                              >
                                <h4 className="text-sm font-semibold text-gray-900 mb-2">
                                  {section.title}
                                </h4>
                                <div className="grid grid-cols-3 gap-4 items-center">
                                  <div className="font-medium text-sm text-gray-700">
                                    Benefit
                                  </div>
                                  <div className="font-medium text-sm text-gray-700">
                                    Basic Limit
                                  </div>
                                  <div className="font-medium text-sm text-gray-700">
                                    Major Limit
                                  </div>
                                  {section.fields.map((field) => {
                                    const benefit = field.single
                                      ? null
                                      : planData.benefits?.find(
                                          (b) =>
                                            b.benefit_code ===
                                            benefitMap[field.name].code
                                        );
                                    const isRoomAndBoardCategory =
                                      field.name === "ben_lmt_ip_room_n_board_cat";
                                    const roomType = String(
                                      benefit?.rnb_room_type ?? ""
                                    ).trim();
                                    const basicLimit = field.planField
                                      ? planData.plan?.[field.planField]
                                      : isRoomAndBoardCategory
                                        ? roomType && roomType !== "NA"
                                          ? roomType
                                          : "N/A"
                                        : benefit?.basic_limit;
                                    // The setup shows the category's basic limit in the Major column.
                                    const majorLimit = isRoomAndBoardCategory
                                      ? benefit?.basic_limit
                                      : benefit?.major_limit;
                                    return (
                                      <React.Fragment key={field.name}>
                                        <div className="text-sm text-gray-700 self-center">
                                          {field.label}
                                        </div>
                                        <div className="text-sm text-gray-900">
                                          {basicLimit === 0 ? 0 : basicLimit || "—"}
                                        </div>
                                        <div className="text-sm text-gray-900">
                                          {field.single
                                            ? null
                                            : majorLimit === 0 ? 0 : majorLimit || "—"}
                                        </div>
                                      </React.Fragment>
                                    );
                                  })}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <EmptyState
                            title="No benefit data"
                            subtitle="No benefit information available for this plan."
                          />
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "member-transaction" && (
                  <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                    <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                      <div>
                        <h3 className="text-base font-semibold text-gray-900">
                          Member Transaction History
                        </h3>
                        <p className="text-xs text-gray-500">
                          Version2 raw-query data mapped from version1.0
                          transaction history logic.
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={fetchMemberTransactions}
                          disabled={memberTransactionsLoading}
                          className="inline-flex items-center gap-2 rounded-md border border-gray-200 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        >
                          <ArrowPathIcon
                            className={clsx(
                              "h-4 w-4",
                              memberTransactionsLoading && "animate-spin"
                            )}
                          />
                          Refresh
                        </button>
                        <button
                          type="button"
                          onClick={handleExportMemberTransactionsCsv}
                          className="inline-flex items-center gap-2 rounded-md border border-gray-200 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
                        >
                          <ArrowDownTrayIcon className="h-4 w-4" />
                          CSV
                        </button>
                        <button
                          type="button"
                          onClick={handleExportMemberTransactionsExcel}
                          className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-2 text-xs font-medium text-white hover:bg-blue-700"
                        >
                          <ArrowDownTrayIcon className="h-4 w-4" />
                          Excel
                        </button>
                      </div>
                    </div>

                    <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
                      <div>
                        <label className="mb-1 block text-xs font-medium text-gray-600">
                          Period
                        </label>
                        <select
                          value={memberTransactionPeriod}
                          onChange={(e) =>
                            setMemberTransactionPeriod(e.target.value)
                          }
                          className="block w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
                        >
                          <option value="all">All</option>
                          <option value="prev">Previous TRNs</option>
                          <option value="curr">Current</option>
                        </select>
                      </div>

                      <div>
                        <label className="mb-1 block text-xs font-medium text-gray-600">
                          Coverage
                        </label>
                        <select
                          value={memberTransactionCoverageFilter}
                          onChange={(e) =>
                            setMemberTransactionCoverageFilter(e.target.value)
                          }
                          className="block w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
                        >
                          <option value="all">All Coverages</option>
                          {memberTransactionCoverageOptions.map((coverage) => (
                            <option key={coverage} value={coverage}>
                              {coverage}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="mb-1 block text-xs font-medium text-gray-600">
                          TRN Status
                        </label>
                        <select
                          value={memberTransactionStatusFilter}
                          onChange={(e) =>
                            setMemberTransactionStatusFilter(e.target.value)
                          }
                          className="block w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
                        >
                          <option value="all">All Statuses</option>
                          {memberTransactionStatusOptions.map((status) => (
                            <option key={status} value={status}>
                              {status}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="mb-1 block text-xs font-medium text-gray-600">
                          Search
                        </label>
                        <input
                          type="text"
                          value={memberTransactionSearch}
                          onChange={(e) =>
                            setMemberTransactionSearch(e.target.value)
                          }
                          placeholder="TRN, LOA, provider, diagnosis..."
                          className="block w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
                        />
                      </div>
                    </div>

                    {memberTransactionsLoading ? (
                      <SkeletonBlock />
                    ) : paginatedMemberTransactions.length > 0 ? (
                      <>
                        <div className="overflow-hidden rounded-lg border border-gray-200">
                          <div className="max-h-[45vh] overflow-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="sticky top-0 bg-gray-50">
                                <tr>
                                  {[
                                    "Action",
                                    "TRN Date",
                                    "TRN No.",
                                    "LOA No.",
                                    "Member",
                                    "Provider",
                                    "Coverage",
                                    "TRN Status",
                                    "LOA Amount",
                                    "Claims Amount",
                                    "Claims Status",
                                    "Chief Complaint",
                                    "ICD-10",
                                    "Diagnosis",
                                    "ICD Category Code",
                                    "ICD Category",
                                  ].map((header) => (
                                    <th
                                      key={header}
                                      className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600"
                                    >
                                      {header}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100 bg-white">
                                {paginatedMemberTransactions.map((row, index) => (
                                  <tr
                                    key={`${row.trans_no}-${index}`}
                                    className="hover:bg-gray-50"
                                  >
                                    <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                      <button
                                        type="button"
                                        onClick={() =>
                                          openTransactionDetails(
                                            row,
                                            "Member Transaction History"
                                          )
                                        }
                                        className="inline-flex items-center gap-1.5 rounded-md border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-100"
                                      >
                                        <EyeIcon className="h-4 w-4" />
                                        View
                                      </button>
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                      {formatDate(row.trans_date, true, "N/A")}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                      {formatDetailText(row.trans_no)}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                      {formatDetailText(row.loa_no)}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                      {formatDetailText(row.member_name)}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                      {formatDetailText(row.provider_name)}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                      {formatDetailText(row.coverage_code)}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                      {formatDetailText(row.trans_flag_desc)}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap text-right">
                                      {formatCurrency(row.loa_amount)}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap text-right">
                                      {formatCurrency(row.claim_amount)}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                      {formatDetailText(row.claim_status)}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900 min-w-[220px]">
                                      {formatDetailText(row.chief_complaint)}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                      {formatDetailText(row.icd_10_col)}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900 min-w-[220px]">
                                      {formatDetailText(row.diagnosis_col)}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                      {formatDetailText(row.categ_code_col)}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900 min-w-[220px]">
                                      {formatDetailText(row.categ_desc_col)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>

                        <div className="mt-3 flex flex-col items-center justify-between gap-3 text-sm text-gray-600 md:flex-row">
                          <div>
                            Showing{" "}
                            {memberTransactionTotalItems > 0
                              ? (memberTransactionCurrentPage - 1) *
                                  memberTransactionItemsPerPage +
                                1
                              : 0}{" "}
                            to{" "}
                            {Math.min(
                              memberTransactionCurrentPage *
                                memberTransactionItemsPerPage,
                              memberTransactionTotalItems
                            )}{" "}
                            of {memberTransactionTotalItems} entries
                          </div>
                          <div className="flex flex-wrap items-center gap-1">
                            <select
                              value={memberTransactionItemsPerPage}
                              onChange={(e) =>
                                setMemberTransactionItemsPerPage(
                                  Number(e.target.value)
                                )
                              }
                              className="rounded-md border border-gray-200 px-2 py-1"
                            >
                              {[10, 25, 50, 100].map((size) => (
                                <option key={size} value={size}>
                                  {size}
                                </option>
                              ))}
                            </select>
                            <button
                              onClick={() => handleMemberTransactionPageChange(1)}
                              disabled={memberTransactionCurrentPage === 1}
                              className="rounded-md border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                            >
                              First
                            </button>
                            <button
                              onClick={() =>
                                handleMemberTransactionPageChange(
                                  memberTransactionCurrentPage - 1
                                )
                              }
                              disabled={memberTransactionCurrentPage === 1}
                              className="rounded-md border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                            >
                              Previous
                            </button>
                            <span className="px-3 py-1.5 text-sm text-gray-700">
                              Page {memberTransactionCurrentPage} of{" "}
                              {memberTransactionTotalPages}
                            </span>
                            <button
                              onClick={() =>
                                handleMemberTransactionPageChange(
                                  memberTransactionCurrentPage + 1
                                )
                              }
                              disabled={
                                memberTransactionCurrentPage ===
                                memberTransactionTotalPages
                              }
                              className="rounded-md border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                            >
                              Next
                            </button>
                            <button
                              onClick={() =>
                                handleMemberTransactionPageChange(
                                  memberTransactionTotalPages
                                )
                              }
                              disabled={
                                memberTransactionCurrentPage ===
                                memberTransactionTotalPages
                              }
                              className="rounded-md border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                            >
                              Last
                            </button>
                          </div>
                        </div>
                      </>
                    ) : (
                      <EmptyState
                        title={
                          memberTransactionSearch ||
                          memberTransactionCoverageFilter !== "all" ||
                          memberTransactionStatusFilter !== "all"
                            ? "No matching transactions"
                            : "No member transactions"
                        }
                        subtitle={
                          memberTransactionSearch ||
                          memberTransactionCoverageFilter !== "all" ||
                          memberTransactionStatusFilter !== "all"
                            ? "Try a different search or filter."
                            : "No transaction history found for this member and period."
                        }
                      />
                    )}
                  </div>
                )}

                {activeTab === "family-members" && (
                  <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                    <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                      <div>
                        <h3 className="text-base font-semibold text-gray-900">
                          Family Members
                        </h3>
                        <p className="text-xs text-gray-500">
                          Export the currently filtered family members list.
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={handleExportFamilyMembersCsv}
                          className="inline-flex items-center gap-2 rounded-md border border-gray-200 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
                        >
                          <ArrowDownTrayIcon className="h-4 w-4" />
                          CSV
                        </button>
                        <button
                          type="button"
                          onClick={handleExportFamilyMembersExcel}
                          className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-2 text-xs font-medium text-white hover:bg-blue-700"
                        >
                          <ArrowDownTrayIcon className="h-4 w-4" />
                          Excel
                        </button>
                      </div>
                    </div>
                    <div className="mb-4 flex items-center gap-2">
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => {
                          setSearchQuery(e.target.value);
                          setCurrentPage(1); // Reset to first page on search
                        }}
                        placeholder="Search family members..."
                        className="block w-full border border-gray-200 rounded-md px-3 py-2 text-sm"
                      />
                    </div>
                    {paginatedMembers.length > 0 ? (
                      <>
                        <div className="overflow-hidden rounded-lg border border-gray-200">
                          <div className="max-h-[40vh] overflow-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="sticky top-0 bg-gray-50">
                                <tr>
                                  {[
                                    "Member ID",
                                    "Last Name",
                                    "First Name",
                                    "Middle Name",
                                    "Suffix",
                                    "Plan Code",
                                    "Plan Name",
                                    "Date of Birth",
                                    "Member Type",
                                    "Status",
                                  ].map((h) => (
                                    <th
                                      key={h}
                                      className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600"
                                    >
                                      {h}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100 bg-white">
                                {paginatedMembers.map((member) => (
                                  <tr
                                    key={member.member_id}
                                    className="hover:bg-gray-50"
                                  >
                                    <td className="px-4 py-2 text-sm text-gray-900">
                                      {member.member_id}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900">
                                      {member.last_name}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900">
                                      {member.first_name}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900">
                                      {member.middle_name || "—"}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900">
                                      {member.suffix || "—"}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900">
                                      {getFamilyMemberPlanLinkValue(
                                        member,
                                        member.plan_code
                                      )}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900">
                                      {getFamilyMemberPlanLinkValue(
                                        member,
                                        member.plan_name
                                      )}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900">
                                      {formatDate(member.date_of_birth)}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900">
                                      {member.member_type === "p"
                                        ? "Principal"
                                        : "Dependent"}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900">
                                      {renderStatusFlagBadge(
                                        member.status_flag,
                                        member.flag_desc ||
                                          member.status_flag_desc
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                        <div className="mt-3 flex flex-col items-center justify-between gap-3 text-sm text-gray-600 md:flex-row">
                          <div>
                            Showing{" "}
                            {totalItems > 0
                              ? (currentPage - 1) * itemsPerPage + 1
                              : 0}{" "}
                            to{" "}
                            {Math.min(currentPage * itemsPerPage, totalItems)}{" "}
                            of {totalItems} entries
                          </div>
                          <div className="flex items-center gap-1">
                            <select
                              value={itemsPerPage}
                              onChange={(e) => {
                                setItemsPerPage(Number(e.target.value));
                                setCurrentPage(1); // Reset to first page
                              }}
                              className="border border-gray-200 rounded-md px-2 py-1"
                            >
                              {[10, 25, 50].map((size) => (
                                <option key={size} value={size}>
                                  {size}
                                </option>
                              ))}
                            </select>
                            <button
                              onClick={() => handlePageChange(1)}
                              disabled={currentPage === 1}
                              className="rounded-md border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                            >
                              First
                            </button>
                            <button
                              onClick={() => handlePageChange(currentPage - 1)}
                              disabled={currentPage === 1}
                              className="rounded-md border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                            >
                              Previous
                            </button>
                            <span className="px-3 py-1.5 text-sm text-gray-700">
                              Page {currentPage} of {totalPages}
                            </span>
                            <button
                              onClick={() => handlePageChange(currentPage + 1)}
                              disabled={currentPage === totalPages}
                              className="rounded-md border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                            >
                              Next
                            </button>
                            <button
                              onClick={() => handlePageChange(totalPages)}
                              disabled={currentPage === totalPages}
                              className="rounded-md border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                            >
                              Last
                            </button>
                          </div>
                        </div>
                      </>
                    ) : (
                      <EmptyState
                        title={
                          searchQuery
                            ? "No matching family members"
                            : "No Family Members"
                        }
                        subtitle={
                          searchQuery
                            ? "Try a different search term."
                            : "There are currently no family members associated with this member."
                        }
                      />
                    )}
                  </div>
                )}

                {activeTab === "family-transaction" && (
                  <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                    <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                      <div>
                        <h3 className="text-base font-semibold text-gray-900">
                          Family Transaction History
                        </h3>
                        <p className="text-xs text-gray-500">
                          Includes principal and dependents based on version1.0
                          family transaction logic.
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={fetchFamilyTransactions}
                          disabled={familyTransactionsLoading}
                          className="inline-flex items-center gap-2 rounded-md border border-gray-200 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        >
                          <ArrowPathIcon
                            className={clsx(
                              "h-4 w-4",
                              familyTransactionsLoading && "animate-spin"
                            )}
                          />
                          Refresh
                        </button>
                        <button
                          type="button"
                          onClick={handleExportFamilyTransactionsCsv}
                          className="inline-flex items-center gap-2 rounded-md border border-gray-200 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
                        >
                          <ArrowDownTrayIcon className="h-4 w-4" />
                          CSV
                        </button>
                        <button
                          type="button"
                          onClick={handleExportFamilyTransactionsExcel}
                          className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-2 text-xs font-medium text-white hover:bg-blue-700"
                        >
                          <ArrowDownTrayIcon className="h-4 w-4" />
                          Excel
                        </button>
                      </div>
                    </div>

                    <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
                      <div>
                        <label className="mb-1 block text-xs font-medium text-gray-600">
                          Period
                        </label>
                        <select
                          value={familyTransactionPeriod}
                          onChange={(e) =>
                            setFamilyTransactionPeriod(e.target.value)
                          }
                          className="block w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
                        >
                          <option value="all">All</option>
                          <option value="prev">Previous TRNs</option>
                          <option value="curr">Current</option>
                        </select>
                      </div>

                      <div>
                        <label className="mb-1 block text-xs font-medium text-gray-600">
                          Coverage
                        </label>
                        <select
                          value={familyTransactionCoverageFilter}
                          onChange={(e) =>
                            setFamilyTransactionCoverageFilter(e.target.value)
                          }
                          className="block w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
                        >
                          <option value="all">All Coverages</option>
                          {familyTransactionCoverageOptions.map((coverage) => (
                            <option key={coverage} value={coverage}>
                              {coverage}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="mb-1 block text-xs font-medium text-gray-600">
                          TRN Status
                        </label>
                        <select
                          value={familyTransactionStatusFilter}
                          onChange={(e) =>
                            setFamilyTransactionStatusFilter(e.target.value)
                          }
                          className="block w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
                        >
                          <option value="all">All Statuses</option>
                          {familyTransactionStatusOptions.map((status) => (
                            <option key={status} value={status}>
                              {status}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="mb-1 block text-xs font-medium text-gray-600">
                          Search
                        </label>
                        <input
                          type="text"
                          value={familyTransactionSearch}
                          onChange={(e) =>
                            setFamilyTransactionSearch(e.target.value)
                          }
                          placeholder="TRN, LOA, provider, diagnosis..."
                          className="block w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
                        />
                      </div>
                    </div>

                    {familyTransactionsLoading ? (
                      <SkeletonBlock />
                    ) : paginatedFamilyTransactions.length > 0 ? (
                      <>
                        <div className="overflow-hidden rounded-lg border border-gray-200">
                          <div className="max-h-[45vh] overflow-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="sticky top-0 bg-gray-50">
                                <tr>
                                  {[
                                    "Action",
                                    "TRN Date",
                                    "TRN No.",
                                    "LOA No.",
                                    "Member",
                                    "Provider",
                                    "Coverage",
                                    "TRN Status",
                                    "LOA Amount",
                                    "Claims Amount",
                                    "Claims Status",
                                    "Chief Complaint",
                                    "ICD-10",
                                    "Diagnosis",
                                    "ICD Category Code",
                                    "ICD Category",
                                  ].map((header) => (
                                    <th
                                      key={header}
                                      className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600"
                                    >
                                      {header}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100 bg-white">
                                {paginatedFamilyTransactions.map((row, index) => (
                                  <tr
                                    key={`${row.trans_no}-${index}`}
                                    className="hover:bg-gray-50"
                                  >
                                    <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                      <button
                                        type="button"
                                        onClick={() =>
                                          openTransactionDetails(
                                            row,
                                            "Family Transaction History"
                                          )
                                        }
                                        className="inline-flex items-center gap-1.5 rounded-md border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-100"
                                      >
                                        <EyeIcon className="h-4 w-4" />
                                        View
                                      </button>
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                      {formatDate(row.trans_date, true, "N/A")}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                      {formatDetailText(row.trans_no)}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                      {formatDetailText(row.loa_no)}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                      {formatDetailText(row.member_name)}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                      {formatDetailText(row.provider_name)}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                      {formatDetailText(row.coverage_code)}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                      {formatDetailText(row.trans_flag_desc)}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap text-right">
                                      {formatCurrency(row.loa_amount)}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap text-right">
                                      {formatCurrency(row.claim_amount)}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                      {formatDetailText(row.claim_status)}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900 min-w-[220px]">
                                      {formatDetailText(row.chief_complaint)}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                      {formatDetailText(row.icd_10_col)}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900 min-w-[220px]">
                                      {formatDetailText(row.diagnosis_col)}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                      {formatDetailText(row.categ_code_col)}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900 min-w-[220px]">
                                      {formatDetailText(row.categ_desc_col)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>

                        <div className="mt-3 flex flex-col items-center justify-between gap-3 text-sm text-gray-600 md:flex-row">
                          <div>
                            Showing{" "}
                            {familyTransactionTotalItems > 0
                              ? (familyTransactionCurrentPage - 1) *
                                  familyTransactionItemsPerPage +
                                1
                              : 0}{" "}
                            to{" "}
                            {Math.min(
                              familyTransactionCurrentPage *
                                familyTransactionItemsPerPage,
                              familyTransactionTotalItems
                            )}{" "}
                            of {familyTransactionTotalItems} entries
                          </div>
                          <div className="flex flex-wrap items-center gap-1">
                            <select
                              value={familyTransactionItemsPerPage}
                              onChange={(e) =>
                                setFamilyTransactionItemsPerPage(
                                  Number(e.target.value)
                                )
                              }
                              className="rounded-md border border-gray-200 px-2 py-1"
                            >
                              {[10, 25, 50, 100].map((size) => (
                                <option key={size} value={size}>
                                  {size}
                                </option>
                              ))}
                            </select>
                            <button
                              onClick={() => handleFamilyTransactionPageChange(1)}
                              disabled={familyTransactionCurrentPage === 1}
                              className="rounded-md border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                            >
                              First
                            </button>
                            <button
                              onClick={() =>
                                handleFamilyTransactionPageChange(
                                  familyTransactionCurrentPage - 1
                                )
                              }
                              disabled={familyTransactionCurrentPage === 1}
                              className="rounded-md border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                            >
                              Previous
                            </button>
                            <span className="px-3 py-1.5 text-sm text-gray-700">
                              Page {familyTransactionCurrentPage} of{" "}
                              {familyTransactionTotalPages}
                            </span>
                            <button
                              onClick={() =>
                                handleFamilyTransactionPageChange(
                                  familyTransactionCurrentPage + 1
                                )
                              }
                              disabled={
                                familyTransactionCurrentPage ===
                                familyTransactionTotalPages
                              }
                              className="rounded-md border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                            >
                              Next
                            </button>
                            <button
                              onClick={() =>
                                handleFamilyTransactionPageChange(
                                  familyTransactionTotalPages
                                )
                              }
                              disabled={
                                familyTransactionCurrentPage ===
                                familyTransactionTotalPages
                              }
                              className="rounded-md border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                            >
                              Last
                            </button>
                          </div>
                        </div>
                      </>
                    ) : (
                      <EmptyState
                        title={
                          familyTransactionSearch ||
                          familyTransactionCoverageFilter !== "all" ||
                          familyTransactionStatusFilter !== "all"
                            ? "No matching transactions"
                            : "No family transactions"
                        }
                        subtitle={
                          familyTransactionSearch ||
                          familyTransactionCoverageFilter !== "all" ||
                          familyTransactionStatusFilter !== "all"
                            ? "Try a different search or filter."
                            : "No transaction history found for this family and period."
                        }
                      />
                    )}
                  </div>
                )}
              </div>

              <div className="sticky bottom-0 z-20 -mx-6 mt-6 flex flex-wrap justify-end gap-3 border-t border-gray-200 bg-white/95 px-6 py-4 backdrop-blur">
                <button
                  type="button"
                  onClick={handleResendWelcomeAlert}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm flex items-center gap-2"
                >
                  <RocketLaunchIcon className="h-5 w-5" />
                  Resend Welcome Text Alert
                </button>

                <button
                  type="button"
                  onClick={() => navigate(`/members/edit/${memberId}`)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm flex items-center gap-2"
                >
                  <PencilSquareIcon className="h-5 w-5" />
                  Edit
                </button>
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 border border-gray-200 rounded-md text-gray-700 hover:bg-gray-50 text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
      <TransactionDetailModal
        isOpen={isTransactionDetailOpen}
        row={selectedTransactionRow}
        sourceLabel={selectedTransactionSource}
        onClose={closeTransactionDetails}
      />
    </AnimatePresence>
  );
};

export default MemberViewModal;

